import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Award,
  CheckCircle2,
  Circle,
  FileQuestion,
  Link2,
  Paperclip,
} from 'lucide-react';

import {
  completeLesson,
  getCoursePlayer,
  getMyTraining,
  type CoursePlayerPayload,
  type PlayerLesson,
  type TakerQuiz,
} from '@/api/training.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { TipTapRenderer } from '@/components/richtext/TipTapRenderer';
import { describeTrainingError } from '@/lib/training/errors';
import { useToast } from '@/hooks/useToast';

/**
 * Course player (Stream C, ADR-025) — lesson sidebar + content pane.
 * Sop-lessons arrive with the approved SOP revision's content already
 * resolved server-side; quizzes in the payload are taker-sanitized
 * (no correct answers on the wire).
 */

export function CoursePlayerPage() {
  const { courseId } = useParams<{ courseId: string }>();

  const playerQuery = useQuery({
    queryKey: ['training', 'course', courseId],
    queryFn: () => getCoursePlayer(courseId ?? ''),
    enabled: Boolean(courseId),
  });
  // Certificate lookup for the completion banner rides the dashboard payload.
  const meQuery = useQuery({ queryKey: ['training', 'me'], queryFn: getMyTraining });

  if (playerQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (playerQuery.error || !playerQuery.data) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div className="flex-1">
          <p className="text-[14px] font-medium text-kw-danger-text">Course not available</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {describeTrainingError(playerQuery.error, 'This course does not exist or you are not enrolled.')}
          </p>
          <Link to="/training" className="mt-3 inline-block text-[13px] text-kw-cerulean hover:underline">
            ← Back to My Training
          </Link>
        </div>
      </Card>
    );
  }

  const certificateCode =
    meQuery.data?.certificates.find((c) => c.courseId === playerQuery.data.course.id)?.certificateCode ??
    null;

  return <Player payload={playerQuery.data} certificateCode={certificateCode} />;
}

function Player({
  payload,
  certificateCode,
}: {
  payload: CoursePlayerPayload;
  certificateCode: string | null;
}) {
  const { course, enrollment, lessons, quizzes, lessonCompletions } = payload;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const completedLessonIds = new Set(lessonCompletions.map((c) => c.lessonId));
  const firstOpenLesson = lessons.find((l) => !completedLessonIds.has(l.id)) ?? lessons[0];
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const currentLesson = lessons.find((l) => l.id === selectedLessonId) ?? firstOpenLesson ?? null;

  const allLessonsDone = lessons.length > 0 && lessons.every((l) => completedLessonIds.has(l.id));
  const finalQuiz = quizzes.find((q) => q.lessonId === null) ?? null;

  const completeMutation = useMutation({
    mutationFn: (lessonId: string) => completeLesson(lessonId),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['training', 'course', course.id] });
      void queryClient.invalidateQueries({ queryKey: ['training', 'me'] });
      if (result.courseCompleted && result.certificateCode) {
        toast.show('Course complete — your certificate is ready.', { variant: 'success' });
      } else {
        toast.show('Lesson marked complete.', { variant: 'success' });
      }
    },
    onError: (err) => {
      toast.show(describeTrainingError(err, 'Could not mark the lesson complete.'), { variant: 'danger' });
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div>
        <Link
          to="/training"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          My Training
        </Link>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="typo-h1">{course.title}</h1>
          {course.description ? <p className="mt-1 text-[14px] text-kw-muted">{course.description}</p> : null}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="neutral">v{course.version}</Badge>
          {enrollment.completedAt ? (
            <Badge variant="success">Completed</Badge>
          ) : enrollment.dueAt ? (
            new Date(enrollment.dueAt).getTime() < Date.now() ? (
              <Badge variant="danger">Overdue</Badge>
            ) : (
              <Badge variant="neutral">
                Due{' '}
                {new Date(enrollment.dueAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </Badge>
            )
          ) : null}
        </div>
      </div>

      {/* Completion banner */}
      {enrollment.completedAt ? (
        <Card className="flex flex-wrap items-center gap-3 border-kw-tag-text/30 bg-kw-emerald-light p-4">
          <Award className="size-5 shrink-0 text-kw-tag-text" />
          <p className="min-w-0 flex-1 text-[14px] text-kw-tag-text">
            You completed this course on{' '}
            {new Date(enrollment.completedAt).toLocaleDateString(undefined, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
            .
          </p>
          {certificateCode ? (
            <Link
              to={`/training/certificates/${certificateCode}`}
              className="text-[13px] font-medium text-kw-cerulean hover:underline"
            >
              View certificate
            </Link>
          ) : null}
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
        {/* Lesson sidebar */}
        <Card className="h-fit p-2">
          <p className="px-2 pb-1 pt-2 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
            Lessons
          </p>
          <ul className="space-y-0.5">
            {lessons.map((lesson, index) => {
              const done = completedLessonIds.has(lesson.id);
              const isCurrent = currentLesson?.id === lesson.id;
              return (
                <li key={lesson.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedLessonId(lesson.id);
                    }}
                    aria-current={isCurrent ? 'true' : undefined}
                    className={`flex w-full items-center gap-2 rounded-kw-md px-2 py-2 text-left text-[13px] ${
                      isCurrent
                        ? 'bg-kw-cerulean-light font-medium text-kw-cerulean-dark'
                        : 'text-kw-shark hover:bg-kw-bg'
                    }`}
                  >
                    {done ? (
                      <CheckCircle2 className="size-4 shrink-0 text-kw-tag-text" />
                    ) : (
                      <Circle className="size-4 shrink-0 text-kw-ghost" />
                    )}
                    <span className="min-w-0 flex-1 truncate">
                      {index + 1}. {lesson.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {finalQuiz ? (
            <div className="mt-1 border-t border-kw-border px-2 py-2">
              <p className="flex items-center gap-2 text-[13px] text-kw-shark">
                <FileQuestion className="size-4 shrink-0 text-kw-muted" />
                Final quiz
              </p>
              <p className="mt-0.5 text-[11px] text-kw-muted">
                {allLessonsDone ? 'Unlocked — finish below.' : 'Unlocks when every lesson is done.'}
              </p>
            </div>
          ) : null}
        </Card>

        {/* Content pane */}
        <div className="min-w-0 space-y-4">
          {currentLesson ? (
            <LessonPane
              courseId={course.id}
              lesson={currentLesson}
              done={completedLessonIds.has(currentLesson.id)}
              quiz={quizzes.find((q) => q.lessonId === currentLesson.id) ?? null}
              completing={completeMutation.isPending}
              onComplete={(lessonId) => {
                completeMutation.mutate(lessonId);
              }}
            />
          ) : (
            <Card className="p-8 text-center text-[13px] text-kw-muted">This course has no lessons yet.</Card>
          )}

          {/* Final quiz card */}
          {finalQuiz && allLessonsDone && !enrollment.completedAt ? (
            <Card className="flex flex-wrap items-center gap-3 p-4">
              <FileQuestion className="size-5 shrink-0 text-kw-cerulean" />
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-medium text-kw-shark">{finalQuiz.title}</p>
                <p className="mt-0.5 text-[12px] text-kw-muted">
                  {finalQuiz.questions.length} question{finalQuiz.questions.length === 1 ? '' : 's'} · pass at{' '}
                  {finalQuiz.passThresholdPercent}%
                  {finalQuiz.maxAttempts !== null
                    ? ` · ${String(finalQuiz.maxAttempts)} attempt${finalQuiz.maxAttempts === 1 ? '' : 's'} max`
                    : ''}
                </p>
              </div>
              <Button
                onClick={() => {
                  void navigate(`/training/courses/${course.id}/quiz/${finalQuiz.id}`);
                }}
              >
                Take final quiz
              </Button>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function LessonPane({
  courseId,
  lesson,
  done,
  quiz,
  completing,
  onComplete,
}: {
  courseId: string;
  lesson: PlayerLesson;
  done: boolean;
  quiz: TakerQuiz | null;
  completing: boolean;
  onComplete: (lessonId: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-kw-border pb-4">
        <div>
          <h2 className="text-[18px] font-semibold text-kw-shark">{lesson.title}</h2>
          <p className="mt-0.5 text-[12px] text-kw-muted">
            {lesson.estimatedMinutes !== null ? `About ${String(lesson.estimatedMinutes)} min` : ''}
            {lesson.estimatedMinutes !== null && lesson.sop ? ' · ' : ''}
            {lesson.sop ? (
              <>
                Wraps SOP{' '}
                <Link to={`/compliance/sops/${lesson.sop.slug}`} className="text-kw-cerulean hover:underline">
                  {lesson.sop.title}
                </Link>
                {lesson.sop.approvedRevision !== null ? ` (rev ${String(lesson.sop.approvedRevision)})` : ''}
              </>
            ) : null}
          </p>
        </div>
        {done ? <Badge variant="success">Done</Badge> : null}
      </div>

      <div className="pt-2">
        <TipTapRenderer doc={lesson.contentJson} emptyText="This lesson has no written content." />
      </div>

      {lesson.mediaLinks.length > 0 ? (
        <LinkList label="Media" icon={<Link2 className="size-4 text-kw-muted" />} links={lesson.mediaLinks} />
      ) : null}
      {lesson.attachmentLinks.length > 0 ? (
        <LinkList
          label="Attachments"
          icon={<Paperclip className="size-4 text-kw-muted" />}
          links={lesson.attachmentLinks}
        />
      ) : null}

      <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-kw-border pt-4">
        {quiz ? (
          <Button
            variant="secondary"
            onClick={() => {
              void navigate(`/training/courses/${courseId}/quiz/${quiz.id}`);
            }}
          >
            <FileQuestion className="size-4" />
            Lesson quiz
          </Button>
        ) : null}
        <Button
          disabled={done}
          isLoading={completing}
          onClick={() => {
            onComplete(lesson.id);
          }}
        >
          <CheckCircle2 className="size-4" />
          {done ? 'Lesson complete' : 'Mark lesson complete'}
        </Button>
      </div>
    </Card>
  );
}

function LinkList({
  label,
  icon,
  links,
}: {
  label: string;
  icon: React.ReactNode;
  links: PlayerLesson['mediaLinks'];
}) {
  return (
    <div className="mt-4 rounded-kw-md bg-kw-bg p-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
        {icon}
        {label}
      </p>
      <ul className="space-y-1">
        {links.map((link, i) => (
          <li key={i}>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="text-[13px] text-kw-cerulean hover:underline"
            >
              {link.title ?? link.url}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
